import { useState } from "react";
import { Star, MessageSquareHeart } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { BottomSheet } from "@/components/BottomSheet";
import { useUI } from "@/context/UIContext";
import { useLang } from "@/context/LanguageContext";
import { useOrder } from "@/context/OrderContext";
import { useSession } from "@/context/SessionContext";
import { restaurantConfig } from "@/config/restaurantConfig";
import { getServiceRatingStatus, serviceRatingTarget, submitServiceRating } from "@/lib/ratings";

function StarRow({ label, value, onChange, testId }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2" data-testid={testId}>
      <span className="text-sm font-semibold">{label}</span>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            onClick={() => onChange(n)}
            className="active:scale-90"
            aria-label={`${label} ${n}`}
            data-testid={`${testId}-star-${n}`}
          >
            <Star className={`h-6 w-6 transition-colors ${n <= value ? "fill-amber-400 text-amber-400" : "text-muted-foreground/40"}`} />
          </button>
        ))}
      </div>
    </div>
  );
}

// Private general service rating for this table session, stored locally (mock).
export function ServiceFeedbackSheet() {
  const { feedbackOpen, closeFeedback } = useUI();
  const { t } = useLang();
  const { order } = useOrder();
  const { sessionId } = useSession();
  const [speed, setSpeed] = useState(0);
  const [care, setCare] = useState(0);
  const [overall, setOverall] = useState(0);
  const [comment, setComment] = useState("");

  const { alreadyRated } = feedbackOpen && sessionId
    ? getServiceRatingStatus(serviceRatingTarget({ sessionId }))
    : { alreadyRated: false };
  const canSubmit = !alreadyRated && speed && care && overall;

  const submit = () => {
    const result = submitServiceRating({
      sessionId,
      orderId: order?.sessionId === sessionId ? order.id : null,
      branchId: restaurantConfig.branch.id,
      scores: { speed, care, overall },
      comment,
    });
    // Not saved: keep the sheet + answers so the guest can retry; never "rated".
    if (result.reason === "storage") {
      toast.error(t("rating_saveFailed"));
      return;
    }
    closeFeedback();
    setSpeed(0); setCare(0); setOverall(0); setComment("");
    if (result.ok) toast.success(t("fb_thanks"), { icon: <MessageSquareHeart className="h-4 w-4" /> });
    else toast(t("fb_already"));
  };

  return (
    <BottomSheet open={feedbackOpen} onClose={closeFeedback} maxHeight="80vh" testId="service-feedback-sheet">
      <div className="px-5 pb-6 pt-1">
        <div className="mb-1 flex items-center gap-3">
          <div className="grid h-11 w-11 place-items-center rounded-2xl brand-gradient text-white">
            <MessageSquareHeart className="h-6 w-6" />
          </div>
          <h2 className="font-heading text-xl font-extrabold">{t("feedbackTitle")}</h2>
        </div>
        <p className="mb-4 text-xs text-muted-foreground">{t("fb_desc")}</p>

        {alreadyRated ? (
          <p className="rounded-2xl border border-border bg-card p-4 text-sm font-semibold" data-testid="fb-already-rated">{t("fb_already")}</p>
        ) : (
          <>
            <div className="divide-y divide-border rounded-2xl border border-border bg-card px-4">
              <StarRow label={t("fb_speed")} value={speed} onChange={setSpeed} testId="fb-speed" />
              <StarRow label={t("fb_care")} value={care} onChange={setCare} testId="fb-care" />
              <StarRow label={t("fb_overall")} value={overall} onChange={setOverall} testId="fb-overall" />
            </div>

            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              maxLength={500}
              placeholder={t("fb_comment")}
              className="mt-4 min-h-[72px] resize-none rounded-2xl"
              data-testid="fb-comment-input"
            />

            <Button
              onClick={submit}
              disabled={!canSubmit}
              className="mt-4 w-full rounded-full brand-gradient py-4 text-base font-bold text-white shadow-soft disabled:opacity-50"
              data-testid="fb-submit-btn"
            >
              {t("fb_submit")}
            </Button>
          </>
        )}
      </div>
    </BottomSheet>
  );
}
